import { useState, useEffect } from 'react';
import { useApp } from '../../context/AppContext';
import { fetchUsers } from '../../api';
import { Button } from '../../components/ui/button';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Avatar, AvatarFallback, AvatarImage } from '../../components/ui/avatar';
import {
  Shield,
  Users,
  FolderKanban,
  ClipboardCheck,
  FileEdit,
  Receipt,
  BarChart3,
  Save,
  RefreshCw,
  Search,
  UserCheck,
  Package,
  MapPin,
  CheckSquare,
  CheckCircle2
} from 'lucide-react';
import { Input } from '../../components/ui/input';

const modules = [
  { key: 'cases', label: 'Case Management', icon: FolderKanban, permissions: ['view', 'create', 'edit', 'delete', 'assign'] },
  { key: 'users', label: 'User Management', icon: Users, permissions: ['view', 'see list', 'create', 'modify', 'delete', 'deactivate', 'blocked'] },
  { key: 'qc', label: 'QC Dashboard', icon: ClipboardCheck, permissions: ['view', 'create lead', 'assign lead', 'verify lead'] },
  { key: 'clients', label: 'Client Management', icon: Users, permissions: ['view', 'create', 'modify', 'delete', 'blocked'] },
  { key: 'formBuilder', label: 'Form Builder Access', icon: FileEdit, permissions: ['view', 'create', 'edit', 'delete'] },
  { key: 'invoices', label: 'Invoices & Payments', icon: Receipt, permissions: ['view', 'create', 'approve'] },
  { key: 'penalties', label: 'Penalties', icon: Shield, permissions: ['view', 'create', 'approve', 'reject'] },
  { key: 'payouts', label: 'Payout Requests', icon: Receipt, permissions: ['view', 'generate request', 'sent request', 'approve', 'reject', 'modify'] },
  { key: 'reports', label: 'Reports & Analytics', icon: BarChart3, permissions: ['view', 'download report and zip case wise only', 'analytics'] },
  { key: 'vendorCreation', label: 'Vendor Creation', icon: UserCheck, permissions: ['view', 'create', 'delete', 'blocked', 'modify'] },
  { key: 'accessControl', label: 'Access Control', icon: Shield, permissions: ['view', 'create', 'edit', 'delete'] },
  { key: 'products', label: 'Products & Rates Management', icon: Package, permissions: ['view', 'create', 'edit', 'delete'] },
  { key: 'manualGeo', label: 'Manual Geo', icon: MapPin, permissions: ['view', 'create', 'edit', 'delete'] },
  { key: 'manualVerification', label: 'Mannual Verification', icon: CheckSquare, permissions: ['view', 'create', 'edit', 'delete'] },
  { key: 'qualityCheck', label: 'Quality Check', icon: CheckCircle2, permissions: ['view', 'create', 'edit', 'delete'] },
];

export default function AccessControl() {
  const { userPermissionsMap, updatePermission } = useApp();
  const [users, setUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [localPermissions, setLocalPermissions] = useState({});
  const [hasChanges, setHasChanges] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    let isMounted = true;
    async function load() {
      try {
        const data = await fetchUsers();
        if (isMounted && Array.isArray(data)) {
          setUsers(data);
          if (data.length > 0) {
            setSelectedUserId(prev => prev || data[0].id);
          }
        }
      } catch (err) {
        console.error('Failed to load users for AccessControl:', err);
      }
    }
    load();
    return () => { isMounted = false; };
  }, []);

  // Initialize local permissions with any existing user overrides
  useEffect(() => {
    if (selectedUserId && userPermissionsMap[selectedUserId]) {
      setLocalPermissions(JSON.parse(JSON.stringify(userPermissionsMap[selectedUserId])));
    } else {
      setLocalPermissions({});
    }
    setHasChanges(false);
  }, [selectedUserId, userPermissionsMap]);

  const togglePermission = (moduleKey, permission) => {
    setLocalPermissions(prev => {
      const modulePerms = prev[moduleKey] || {};
      return {
        ...prev,
        [moduleKey]: {
          ...modulePerms,
          [permission]: !modulePerms[permission]
        }
      };
    });
    setHasChanges(true);
  };

  const hasPerm = (moduleKey, permission) => {
    return localPermissions[moduleKey]?.[permission] || false;
  };

  const resetPermissions = () => {
    if (selectedUserId && userPermissionsMap[selectedUserId]) {
      setLocalPermissions(JSON.parse(JSON.stringify(userPermissionsMap[selectedUserId])));
    } else {
      setLocalPermissions({});
    }
    setHasChanges(false);
  };

  const savePermissions = () => {
    if (!selectedUserId) return;
    // Save to AppContext
    Object.keys(localPermissions).forEach(moduleKey => {
      Object.keys(localPermissions[moduleKey]).forEach(action => {
        updatePermission(selectedUserId, moduleKey, action, localPermissions[moduleKey][action]);
      });
    });
    setHasChanges(false);
  };

  const filteredUsers = users.filter(u =>
    (u.name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
    (u.email || '').toLowerCase().includes(searchQuery.toLowerCase())
  );

  const selectedUser = users.find(u => u.id === selectedUserId) || users[0];

  return (
    <div className="space-y-6 animate-fade-in" data-testid="access-control-page">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Access Control (User Based)</h1>
          <p className="text-sm text-slate-500 mt-1">Manage custom permissions and access rights per user</p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            onClick={resetPermissions}
            className="bg-white border-slate-200 gap-2"
            disabled={!hasChanges}
          >
            <RefreshCw className="w-4 h-4" />
            Reset
          </Button>
          <Button
            className="bg-blue-600 hover:bg-blue-700 gap-2"
            onClick={savePermissions}
            disabled={!hasChanges}
            data-testid="save-permissions-btn"
          >
            <Save className="w-4 h-4" />
            Save Changes
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* User Search & List */}
        <div className="crm-card p-4">
          <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-4">Users</h3>

          <div className="relative mb-4">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <Input
              type="text"
              placeholder="Search users..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 bg-slate-50/50 border-slate-200 text-sm"
            />
          </div>

          <ScrollArea className="h-[500px]">
            <div className="space-y-2">
              {filteredUsers.map((user) => (
                <div
                  key={user.id}
                  onClick={() => setSelectedUserId(user.id)}
                  className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${selectedUserId === user.id
                    ? 'bg-blue-50 border border-blue-500/30'
                    : 'bg-white hover:bg-slate-100/50 border border-transparent'
                    }`}
                  data-testid={`user-${user.id}`}
                >
                  <Avatar className="w-8 h-8">
                    <AvatarImage src={user.avatar} />
                    <AvatarFallback>{user.name.charAt(0)}</AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-medium truncate ${selectedUserId === user.id ? 'text-blue-600' : 'text-slate-900'}`}>{user.name}</p>
                    <p className="text-xs text-slate-500 truncate">{user.role}</p>
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>
        </div>

        {/* Permissions Grid */}
        <div className="lg:col-span-3 crm-card p-6">
          {selectedUser ? (
            <>
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-lg font-semibold text-slate-900">Permissions for {selectedUser.name}</h3>
                  <p className="text-sm text-slate-500 mt-1">Configure user-specific custom overrides (Overrides Role Defaults)</p>
                </div>
                {hasChanges && (
                  <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                    Unsaved Changes
                  </Badge>
                )}
              </div>

              <div className="space-y-6">
                {modules.map((module) => {
                  const Icon = module.icon;
                  return (
                    <div key={module.key} className="p-4 rounded-lg bg-white border border-slate-200">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center">
                          <Icon className="w-4 h-4 text-blue-600" />
                        </div>
                        <h4 className="text-sm font-semibold text-slate-900">{module.label}</h4>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                        {module.permissions.map((permission) => (
                          <div key={permission} className="flex items-center justify-between flex-wrap gap-2">
                            <span className="text-sm text-slate-500 capitalize">{permission}</span>
                            <Switch
                              checked={hasPerm(module.key, permission)}
                              onCheckedChange={() => togglePermission(module.key, permission)}
                              data-testid={`permission-${module.key}-${permission}`}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          ) : (
            <div className="flex flex-col items-center justify-center h-full text-slate-500">
              <Shield className="w-12 h-12 mb-4 text-slate-300" />
              <p>Select a user to manage their custom permissions.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
